import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Power,
  Users,
  CheckCircle,
  Download,
  MoreHorizontal,
  StretchVertical,
  Copy,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import BlockchainFunction from "../blockchain/blockchain";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { toast } from "sonner";
import TaskBuilder from "./TaskBuilder";
import { Progress } from "../ui/progress";
import { DASHBOARD_ROUTES } from "../../constants/constant";
import {
  useChangeCampaignsTaskStatusApiCallMutation,
  useGetTasksAnalyticsApiCallQuery,
  useGetTasksApiCallQuery,
} from "../../services/api/tasks/tasks.api";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";
import { Task } from "../../services/api/tasks/tasks.type";
import {
  useGetTransactionsApiCallQuery,
  useLazyGetTransactionsApiCallQuery,
} from "../../services/api/transactions/transactions.api";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useAccount, useDisconnect, useWalletClient } from "wagmi";
import { TransactionsType } from "../../services/api/transactions/transactions.type";
import {
  useGetCampaignsAmountByIdApiCallQuery,
  useGetCampaignsByIdApiCallQuery,
} from "../../services/api/campaigns/campaigns.api";
import Web3 from "web3";
import useDebounce from "../../hooks/useDebounce";

const CampaignDashboard: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantCompletionFilter, setParticipantCompletionFilter] =
    useState<"all" | "completed" | "not-completed">("all");
  const [participantClaimFilter, setParticipantClaimFilter] = useState<
    "all" | "claimed" | "not-claimed"
  >("all");
  const { openConnectModal } = useConnectModal();
  const { disconnect } = useDisconnect();
  const [popupData, setPopupData] = useState<TransactionsType[] | []>([]);
  const { data: walletClient } = useWalletClient();
  const web3 = new Web3(window.ethereum as any); // const [popupData, setPopupData] = useState<TransactionsType[]>(
  //   Array.from({ length: 25 }, (_, i) => ({
  //     _id: String(i + 1),
  //     createdAt: new Date().toISOString(),
  //     claimedAmountByUser: 0,
  //     transactionType: "",
  //     hash: "",
  //     from: "",
  //     walletAddress: `0xWalletAddress${i + 1}`,
  //     amount: 0,
  //     status: "",
  //     isClaimed: false,
  //     isCompleted: false,
  //     campaignId: { name: "" },
  //   }))
  // );

  const claimTypeFromUrl = searchParams.get("claimType");
  const debouncedParticipantSearch = useDebounce(participantSearch, 500);
  const { multisendUsdt } = BlockchainFunction();

  const { address, isConnected } = useAccount();
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  console.log("multisendUsdt", isConnected, showPopup);

  const {
    data: tasksData,
    isLoading: tasksLoading,
    isError,
  } = useGetTasksApiCallQuery({
    campaignId: id,
    page: page,
    limit: 10,
  });
  const { data: analyticsTasks } = useGetTasksAnalyticsApiCallQuery({
    campaignId: id,
  });

  const { data: campaignData } = useGetCampaignsAmountByIdApiCallQuery({
    campaignId: id || "",
  });

  const { data: campaignByIdData } = useGetCampaignsByIdApiCallQuery(
    { campaignId: id || "" },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const campaign = campaignByIdData?.result;
  const claimType = campaign?.claimType || claimTypeFromUrl;

  const {
    currentData: participantsData,
    isLoading: participantsLoading,
    isFetching: participantsFetching,
  } = useGetTransactionsApiCallQuery(
    {
      campaignId: id,
      page: participantsPage,
      limit: 10,
      transactionType: "JOINED",
      search: debouncedParticipantSearch || undefined,
      isCompleted:
        participantCompletionFilter === "all"
          ? undefined
          : participantCompletionFilter === "completed",
      isClaimed:
        participantClaimFilter === "all"
          ? undefined
          : participantClaimFilter === "claimed",
    },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const [getTransactions] = useLazyGetTransactionsApiCallQuery();

  const [changeTaskStatus] = useChangeCampaignsTaskStatusApiCallMutation();

  const [showTaskBuilder, setShowTaskBuilder] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const getTaskTypeLabel = (type: string) => {
    console.log(type);
    const labels = {
      Follow: "Follow",
      Post: "Post",
      Reshare: "Reshare",
      Deposit: "Deposit",
      Like: "Like",
      Join: "Join",
      Comment: "Comment",
      Buy: "Buy",
      "Create Account": "Create Account",
      Login: "Login",
      KYC: "KYC",
    };
    return labels[type as keyof typeof labels];
  };

  // const handleTaskSave = (taskData: Omit<Task, "id">) => {
  //   if (editingTask) {
  //     setTasks((prev) =>
  //       prev.map((task) =>
  //         task.id === editingTask.id ? { ...taskData, id: task.id } : task
  //       )
  //     );
  //     toast.success("Task updated successfully");
  //   } else {
  //     const newTask: Task = {
  //       ...taskData,
  //       id: Date.now().toString(),
  //     };
  //     setTasks((prev) => [...prev, newTask]);
  //     toast.success("Task created successfully");
  //   }

  //   setShowTaskBuilder(false);
  //   setEditingTask(null);
  // };

  const handleTaskDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await changeTaskStatus({
        taskId: deleteTargetId as string,
        status: "Delete",
      }).unwrap();
      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting task");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleTaskToggleStatus = async (taskId: string, status: string) => {
    try {
      const data = await changeTaskStatus({
        taskId: taskId as string,
        status: status === "Active" ? "Block" : "Active",
      }).unwrap();
      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting task");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      setIsExporting(true);

      const exportLimit = participantsData?.result?.total || 10;

      const response = await getTransactions({
        transactionType: "JOINED",
        page: 1,
        limit: exportLimit,
        campaignId: id,
        search: debouncedParticipantSearch || undefined,
        isCompleted:
          participantCompletionFilter === "all"
            ? undefined
            : participantCompletionFilter === "completed",
        isClaimed:
          participantClaimFilter === "all"
            ? undefined
            : participantClaimFilter === "claimed",
      }).unwrap();

      // const data: Transaction[] = response?.result?.data || [];

      if (response.result?.docs.length === 0) {
        toast.info("No data available to export");
        return;
      }

      const exportData = response?.result?.docs.map((row: any) => ({
        ID: row._id,
        "Transaction Type": row.transactionType,
        Hash: row.hash || "-",
        "Wallet Address": row.walletAddress,
        Status: row.status,
        "Is Completed": row.isCompleted ? "Yes" : "No",
        "Is Claimed": row.isClaimed ? "Yes" : "No",
        "Claimed Amount": row.claimedAmountByUser,
        "Created At": new Date(row.createdAt).toLocaleString(),

        // ✅ Flatten campaignId nested object
        "Campaign Name": row.campaignId?.name || "-",
        "Campaign Description": row.campaignId?.description || "-",
        "Campaign Status": row.campaignId?.status || "-",
        Token: row.campaignId?.tokenSymbol || "-",
        "Airdrop Amount": row.campaignId?.airdropAmount ?? "-",
        "Campaign Start": row.campaignId?.startDate
          ? new Date(row.campaignId.startDate).toLocaleDateString()
          : "-",
        "Campaign End": row.campaignId?.endDate
          ? new Date(row.campaignId.endDate).toLocaleDateString()
          : "-",
      }));

      // Convert JSON → worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData!);

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      saveAs(blob, `participants_${Date.now()}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = (fetchError?.data as ApiError) || {};
      toast.error(errorData?.responseMessage || "Error exporting file");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDistribution = async () => {
    if (openConnectModal) {
      openConnectModal();
    }
  };

  const handleClosePopup = () => {
    disconnect();

    setShowPopup(false);
  };

  const getPopupTableData = useCallback(async () => {
    try {
      const response = await getTransactions({
        campaignId: id,
        limit: 10,
        page: 1,
        transactionType: "JOINED",
        isCompleted: true,
        isClaimed: false,
      }).unwrap();
      // setShowPopup(true);

      if (response.responseCode === 200) {
        setShowPopup(true);

        setPopupData(response?.result?.docs || []);
      }
    } catch (error) {
      // Keep silent; popup opens only after explicit connect action.
    }
    // setParticipantsData(response);
  }, [getTransactions, id]);

  console.log(popupData);
  useEffect(() => {
    if (isConnected && address) {
      // Only open when explicitly asked
      getPopupTableData();
      setShowPopup(true);
    } else {
      // Ensure popup is always closed if disconnected
      setShowPopup(false);
    }
  }, [isConnected, address, getPopupTableData]);

  useEffect(() => {
    setParticipantsPage(1);
  }, [
    debouncedParticipantSearch,
    participantCompletionFilter,
    participantClaimFilter,
  ]);

  if (isConnected && !walletClient) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={DASHBOARD_ROUTES.CAMPAIGNS}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Managing: {campaign?.name || "Campaign"}
            </h1>
            <p className="text-gray-600 mt-1">
              Detailed campaign control and monitoring
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Badge
            className={
              campaign?.status?.toLowerCase() === "active"
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }
          >
            {campaign?.status
              ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)
              : "-"}
          </Badge>
        </div>
      </div>

      {/* Campaign Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Participants</p>
                <p className="text-2xl font-semibold">
                  {/* {campaign.participants.toLocaleString()} */}
                  {analyticsTasks?.result?.totalParticipants}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Tasks</p>
                <p className="text-2xl font-semibold">
                  {/* {
                    tasksData?.result?.docs?.filter(
                      (t) => t.status.toLowerCase() === "active"
                    ).length
                  } */}
                  {analyticsTasks?.result?.totalActiveTasks}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Reward Pool</p>
                <p className="text-2xl font-semibold">
                  {/* {campaign.tokenSymbol} {campaign.totalPool.toLocaleString()} */}
                  {analyticsTasks?.result?.totalReward}
                </p>
              </div>
              <div className="text-purple-600 text-2xl font-bold">💎</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Progress</p>
                <p className="text-2xl font-semibold">
                  {analyticsTasks?.result?.progress}%
                </p>
              </div>
              <div className="w-full mt-2">
                <Progress
                  value={analyticsTasks?.result?.progress}
                  className="h-2"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="tasks" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tasks">Task Management</TabsTrigger>
          <TabsTrigger value="participants">Participants</TabsTrigger>
          {/* <TabsTrigger value="distribution">Distribution</TabsTrigger> */}
        </TabsList>

        <TabsContent value="tasks" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Campaign Tasks</CardTitle>
                  <CardDescription>
                    Manage tasks that participants need to complete
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowTaskBuilder(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task Type</TableHead>
                      <TableHead>Instruction</TableHead>
                      <TableHead>Reward</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasksLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-16 text-center">
                          Loading....
                        </TableCell>
                      </TableRow>
                    ) : (
                      !isError &&
                      tasksData?.result &&
                      tasksData?.result?.docs?.length > 0 &&
                      tasksData.result?.docs.map((task) => (
                        <TableRow key={task._id}>
                          <TableCell>
                            <Badge variant="outline">
                              {getTaskTypeLabel(task.taskType)}
                              {(task.taskType === "Buy" ||
                                task.taskType === "Deposit") &&
                                task.amount && (
                                  <span className="text-xs text-gray-600 ml-1">
                                    (Min: {task.amount})
                                  </span>
                                )}

                              {/* 🔹 Show New / Existing User for BinGold tasks */}
                              {(task.taskType === "Create Account" ||
                                task.taskType === "Buy" ||
                                task.taskType === "Deposit") &&
                                task.isNewUser !== undefined &&
                                task.isNewUser !== null && (
                                  <span className="text-xs text-gray-600 ml-1">
                                    (
                                    {task.isNewUser
                                      ? "New User"
                                      : "Existing User"}
                                    )
                                  </span>
                                )}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md truncate">
                            {task.instructions}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {task.reward} {campaign?.tokenSymbol || "TOKEN"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                task.status.toLowerCase() === "active"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-200 text-gray-800"
                              }
                            >
                              {task.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditingTask(task);
                                    setShowTaskBuilder(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleTaskToggleStatus(
                                      task._id,
                                      task.status,
                                    )
                                  }
                                >
                                  <Power className="h-4 w-4 mr-2" />
                                  {task.status?.toLowerCase() === "active"
                                    ? "Deactivate"
                                    : "Activate"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setDeleteTargetId(task._id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {tasksData?.result && tasksData?.result?.pages > 1 && (
                  <div className="flex justify-end items-center mt-4 gap-2">
                    <Button
                      variant="outline"
                      disabled={page === 1}
                      onClick={() => setPage((prev) => prev - 1)}
                    >
                      Previous
                    </Button>
                    <span>
                      Page {page} of {tasksData.result?.pages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page === tasksData.result.pages}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}

                {isError && (
                  <div className="text-center py-12">
                    {/* <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" /> */}
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No taskss found
                    </h3>
                  </div>
                )}

                {/* popup for delete confirmation  */}
                {deleteTargetId && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl max-w-sm w-full">
                      <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                        Confirm Delete
                      </h2>
                      <p className="mb-6 text-gray-600 dark:text-gray-300">
                        Are you sure you want to delete this campaign?
                      </p>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => setDeleteTargetId(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                          onClick={handleTaskDelete}
                        >
                          Yes, Delete{" "}
                          {isDeleteLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {tasksData?.result?.docs.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No tasks created yet
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Add tasks for participants to complete and earn rewards
                  </p>
                  <Button onClick={() => setShowTaskBuilder(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Task
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="participants" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Campaign Participants</CardTitle>
                  <CardDescription>
                    Monitor participant progress and status
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    disabled={isExporting}
                    variant="outline"
                    size="sm"
                    onClick={exportCSV}
                    className="cursor-pointer"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  <Link to={DASHBOARD_ROUTES?.USERS}>
                    <Button variant="outline" size="sm">
                      <Users className="h-4 w-4 mr-2" />
                      View All Participants
                    </Button>
                  </Link>

                  {claimType === "Completion" && (
                    <Button
                      onClick={() => handleDistribution()}
                      // variant="outline"
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <StretchVertical />
                      Distribution
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Search by wallet or referrer..."
                  value={participantSearch}
                  onChange={(e) => setParticipantSearch(e.target.value)}
                />

                <select
                  className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={participantCompletionFilter}
                  aria-label="Filter participants by completion status"
                  title="Filter participants by completion status"
                  onChange={(e) =>
                    setParticipantCompletionFilter(
                      e.target.value as "all" | "completed" | "not-completed",
                    )
                  }
                >
                  <option value="all">All Completion</option>
                  <option value="completed">Completed</option>
                  <option value="not-completed">Not Completed</option>
                </select>

                <select
                  className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={participantClaimFilter}
                  aria-label="Filter participants by claim status"
                  title="Filter participants by claim status"
                  onChange={(e) =>
                    setParticipantClaimFilter(
                      e.target.value as "all" | "claimed" | "not-claimed",
                    )
                  }
                >
                  <option value="all">All Claim Status</option>
                  <option value="claimed">Claimed</option>
                  <option value="not-claimed">Not Claimed</option>
                </select>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Wallet Address</TableHead>
                      {/* <TableHead>Tasks Completed</TableHead> */}
                      <TableHead>Current Reward</TableHead>
                      <TableHead>Complition Status</TableHead>
                      <TableHead>Claimed Status</TableHead>
                      <TableHead>Referred By</TableHead>
                      <TableHead>Joined Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {participantsLoading || participantsFetching ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-16 text-center">
                          Loading participants...
                        </TableCell>
                      </TableRow>
                    ) : participantsData?.result?.docs?.length ? (
                      participantsData?.result?.docs?.map((participant) => (
                        <TableRow key={participant._id}>
                          <TableCell className="font-mono flex">
                            {participant.walletAddress}
                            {participant.walletAddress ? (
                              <Copy
                                className=" h-4 cursor-pointer text-gray-500 hover:text-black ml-2"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    participant.walletAddress,
                                  );
                                  toast.success("Wallet address copied!");
                                }}
                              />
                            ) : (
                              <span>--</span>
                            )}{" "}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">
                                {participant?.claimedAmountByUser}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              {participant?.isCompleted ? "True" : "False"}
                            </div>
                          </TableCell>
                          <TableCell>
                            {/* <Badge
                              className={
                                participant.status === "eligible"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }
                            >
                              {participant.isClaimed || "false"}
                            </Badge> */}

                            {participant.isClaimed ? "True" : "False"}
                          </TableCell>

                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">
                                {participant?.referredBy}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {new Date(
                              participant.createdAt,
                            ).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-16 text-center">
                          No data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {participantsData?.result &&
                  participantsData?.result?.pages > 1 && (
                    <div className="flex justify-end items-center mt-4 gap-2">
                      <Button
                        variant="outline"
                        disabled={participantsPage === 1}
                        onClick={() => setParticipantsPage((prev) => prev - 1)}
                      >
                        Previous
                      </Button>
                      <span>
                        Page {participantsPage} of {participantsData.result?.pages}
                      </span>
                      <Button
                        variant="outline"
                        disabled={
                          participantsPage === participantsData.result.pages
                        }
                        onClick={() => setParticipantsPage((prev) => prev + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* <TabsContent value="distribution" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Token Distribution</CardTitle>
              <CardDescription>
                Manage token distribution for this campaign
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              Distribution Status
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Distribution Status
                  </h4>
                  <Badge
                    className={
                      canInitiateDistribution()
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }
                  >
                    {canInitiateDistribution() ? "Ready" : "Pending"}
                  </Badge>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Eligible Claimers
                  </h4>
                  <p className="text-2xl font-semibold">
                    {
                      mockParticipants.filter((p) => p.status === "eligible")
                        .length
                    }
                  </p>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Total to Distribute
                  </h4>
                  <p className="text-2xl font-semibold">
                    {mockParticipants
                      .filter((p) => p.status === "eligible")
                      .reduce((sum, p) => sum + p.currentReward, 0)
                      .toLocaleString()}{" "}
                    {campaign.tokenSymbol}
                  </p>
                </div>
              </div>

              Distribution Controls
              <div className="p-6 border rounded-lg bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">
                        Initiate Token Distribution
                      </h4>
                      <p className="text-gray-600 text-sm">
                        This action will begin the token distribution process
                        for all eligible participants. This action cannot be
                        undone.
                      </p>
                    </div>

                    {canInitiateDistribution() ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button className="bg-red-600 hover:bg-red-700">
                            <PlayCircle className="h-4 w-4 mr-2" />
                            INITIATE DISTRIBUTION
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Confirm Token Distribution
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              This will distribute tokens to{" "}
                              {
                                mockParticipants.filter(
                                  (p) => p.status === "eligible"
                                ).length
                              }{" "}
                              eligible participants. This action cannot be
                              undone.
                              <div className="mt-4 space-y-2">
                                <Label htmlFor="confirmation">
                                  Type "DISTRIBUTE" to confirm:
                                </Label>
                                <Input
                                  id="confirmation"
                                  value={distributionConfirmation}
                                  onChange={(e) =>
                                    setDistributionConfirmation(e.target.value)
                                  }
                                  placeholder="Type DISTRIBUTE here"
                                  className="font-mono"
                                />
                              </div>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              onClick={() => setDistributionConfirmation("")}
                            >
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleInitiateDistribution}
                              className="bg-red-600 hover:bg-red-700"
                              disabled={
                                distributionConfirmation !== "DISTRIBUTE"
                              }
                            >
                              Initiate Distribution
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <Button disabled>Distribution Not Available</Button>
                    )}
                  </div>
                </div>
              </div>

              Download Report
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Campaign Report
                      </h4>
                      <p className="text-sm text-gray-600">
                        Download a detailed CSV report of all participants and
                        their rewards
                      </p>
                    </div>
                    <Button variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Download Report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>

      {/* Task Builder Modal */}
      {showTaskBuilder && (
        <TaskBuilder
          task={editingTask}
          // onSave={handleTaskSave}
          id={id || ""}
          onCancel={() => {
            setShowTaskBuilder(false);
            setEditingTask(null);
          }}
        />
      )}

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-hidden">
          <div className="bg-white w-full max-w-[1300px] max-h-[90vh] overflow-y-auto rounded-xl shadow-lg p-6 relative">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold">Wallet Connected 🎉</h2>
              <button
                className="text-gray-500 hover:text-gray-800"
                onClick={() => {
                  setShowPopup(false);

                  handleClosePopup();
                }}
              >
                ✕
              </button>
            </div>

            {/* Table */}
            <div className="rounded-md border overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 border w-[50px] text-center">Select</th>
                    <th className="text-left p-3 border">Wallet Address</th>
                    {/* <th className="text-left p-3 border">Current Reward</th>
              <th className="text-left p-3 border">Completion Status</th>
              <th className="text-left p-3 border">Claimed Status</th>
              <th className="text-left p-3 border">Referred By</th>
              <th className="text-left p-3 border">Joined Date</th> */}
                  </tr>
                </thead>
                <tbody>
                  {popupData?.length > 0 ? (
                    popupData?.map((participant) => {
                      const isSelected = selectedRows.includes(
                        participant.walletAddress,
                      );
                      return (
                        <tr
                          key={participant._id}
                          className={`hover:bg-gray-50 ${
                            isSelected ? "bg-green-50" : ""
                          }`}
                        >
                          <td className="p-3 border text-center">
                            <input
                              type="checkbox"
                              aria-label={`Select participant ${participant.walletAddress}`}
                              title={`Select participant ${participant.walletAddress}`}
                              checked={isSelected}
                              onChange={() => {
                                if (isSelected) {
                                  // unselect
                                  setSelectedRows((prev) =>
                                    prev.filter(
                                      (id) => id !== participant.walletAddress,
                                    ),
                                  );
                                } else {
                                  // select max 20
                                  if (selectedRows.length < 20) {
                                    setSelectedRows((prev) => [
                                      ...prev,
                                      participant.walletAddress,
                                    ]);
                                  } else {
                                    alert(
                                      "You can select maximum 20 wallets only!",
                                    );
                                  }
                                }
                              }}
                            />
                          </td>
                          <td className="p-3 border font-mono flex">
                            {participant.walletAddress}
                            {participant.walletAddress ? (
                              <Copy
                                className=" h-4 cursor-pointer text-gray-500 hover:text-black ml-2"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    participant.walletAddress,
                                  );
                                  toast.success("Wallet address copied!");
                                }}
                              />
                            ) : (
                              <span>--</span>
                            )}{" "}
                          </td>
                          {/* <td className="p-3 border">{participant?.claimedAmountByUser}</td>
                  <td className="p-3 border">{participant?.isCompleted ? "True" : "False"}</td>
                  <td className="p-3 border">{participant.isClaimed ? "True" : "False"}</td>
                  <td className="p-3 border">{participant?.referredBy}</td>
                  <td className="p-3 border">{new Date(participant.createdAt).toLocaleDateString()}</td> */}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="p-3 border">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Selected: {selectedRows.length} / 20
              </p>
              <button
                className="px-4 py-2 rounded bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
                disabled={selectedRows.length === 0}
                onClick={async () => {
                  console.log("Selected IDs:", selectedRows);
                  const multisendValue = {
                    recipients: selectedRows,
                    amount: campaignData?.result?.data,
                  };
                  await multisendUsdt(
                    web3,
                    address,
                    multisendValue,
                    id,
                  );
                  getPopupTableData();
                  setShowPopup(true);
                }}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDashboard;
